import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  HStack,
  Heading,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  StackProps,
  UseDisclosureReturn,
  VStack,
  styled,
  useDisclosure
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { MotionProps, motion } from "framer-motion"
import React, { useEffect, useMemo, useState } from "react"
import { match } from "react-router-dom"
import { usePreviousDistinct } from "react-use"
import IconButton, { IconButtonProps, Indicator } from "../button/IconButton"
import Link from "../link/Link"
import Tooltip from "../tooltip/Tooltip"

const MotionStack = motion(Stack)

// We'll override the "icon" prop and make the "aria-label" optional which
// will be handled by default using the option label.
type PartialIconButtonProps = Omit<IconButtonProps, "icon" | "aria-label"> &
  Partial<Pick<IconButtonProps, "aria-label">>

// Children will be handled by routes.
export type AugmentationToolbarChildren = Omit<
  AugmentationToolbarOption,
  "icon" | "renderIcon" | "to" | "ToolbarComponent"
>[]

type ToolbarContext = "toolbar" | "dropdown"

type RenderIcon = (icon: JSX.Element, context: ToolbarContext) => JSX.Element

type AugmentationComponent = (props: {
  toolbar: AugmentationToolbarHookReturn
  match: match<any>
}) => JSX.Element

type AugmentationLabelComponent = (props: { match: match<any> }) => JSX.Element

type AugmentationNavComponent = (props: {
  option: AugmentationToolbarOptionNode
}) => JSX.Element

export type AugmentationToolbarOption = {
  key: string
  // The compositeKey key includes ancestor keys joined by "/"
  compositeKey?: string
  icon: IconName
  renderIcon?: RenderIcon
  label: string
  to?: string
  // Optional method for indicating if option is selected when option does not
  // supply a AugmentationComponent.
  isSelected?: boolean
  // Probably change to accept the toolbar option and be responsible for
  // rendering the IconButton
  ToolbarComponent?: (props: AugmentationToolbarButtonProps) => JSX.Element
  // Component used to render the augmentation within the panel. This is optional
  // as an option lacking this prop will not integrate with the panel, nor will it
  // be rendered withing the toolbar island, but adjacent to it.
  AugmentationComponent?: AugmentationComponent
  // Component for rendering a label in breadcrumbs.
  AugmentationLabelComponent?: AugmentationLabelComponent
  // Component for rendering additional nav controls in the header.
  AugmentationNavComponent?: AugmentationNavComponent
  children?: AugmentationToolbarChildren
  parent?: AugmentationToolbarOptionNode | null
} & PartialIconButtonProps

// Allow passing either a single option or an array of options (for groups).
export type AugmentationToolbarOptions = (
  | AugmentationToolbarOption
  | null
  | (AugmentationToolbarOption | null)[]
)[]

export type AugmentationToolbarOptionNode =
  | AugmentationToolbarOption
  | AugmentationToolbarChildren[number]

export type AugmentationToolbarHookReturn = {
  stack: AugmentationToolbarOptionNode[]
  options: AugmentationToolbarOption[]
  structuredOptions: AugmentationToolbarOptions
  selectedOption: AugmentationToolbarOptionNode | null
  selectedKey: AugmentationToolbarOptionNode["key"] | null
  onSelect: (key: AugmentationToolbarOptionNode["key"] | null) => void
}

export function useAugmentationToolbar(
  structuredOptions: AugmentationToolbarOptions
): AugmentationToolbarHookReturn {
  const [stack, setStack] = useState<AugmentationToolbarOptionNode[]>([])

  // Filter out all null options.
  const filteredStructuredOptions = useMemo(() => {
    return (
      structuredOptions
        // Filter out null options within groups.
        .map((option) =>
          Array.isArray(option) ? option.filter(Boolean) : option
        )
        // Filter out null or empty groups.
        .filter((option) =>
          Array.isArray(option) ? option.length > 0 : option
        ) as AugmentationToolbarOption[][]
    )
  }, [structuredOptions])

  const [options, optionsMap] = useMemo(() => {
    // Flatten the structured options into a single array of options (though these still
    // nest with children). We keep the structured options for rendering the tree.
    const options = filteredStructuredOptions.flatMap((option) =>
      Array.isArray(option) ? option : [option]
    ) as AugmentationToolbarOption[]

    const optionsMap: Map<string, AugmentationToolbarOptionNode> = new Map()

    // Add parent reference to children options for easier traversal up the tree.
    function extendOptions(
      options: AugmentationToolbarOptionNode[],
      parent: AugmentationToolbarOptionNode | null = null
    ): AugmentationToolbarOptionNode[] {
      return options.map((option) => {
        // Create a composite key that includes parent keys for easier selection.
        const compositeKey = parent
          ? [parent.compositeKey, option.key].join("/")
          : option.key
        const newOption = { ...option, parent, compositeKey }
        // Side-effect: add to map as we go.
        optionsMap.set(compositeKey, newOption)

        if (newOption.children) {
          newOption.children = extendOptions(newOption.children, newOption)
        }
        return newOption
      })
    }

    const extendedOptions = extendOptions(options)

    return [extendedOptions as AugmentationToolbarOption[], optionsMap]
  }, [filteredStructuredOptions])

  function handleSelectKey(key: AugmentationToolbarOptionNode["key"] | null) {
    const option = optionsMap.get(key || "")

    // Get the stack of options from the selected option up to the root.
    function getStackFromOption(option: AugmentationToolbarOptionNode) {
      const stack = [option]
      while (stack[0]?.parent) {
        const parent = stack[0].parent
        // Add option to the start of the stack.
        stack.unshift(parent)
      }
      return stack
    }

    if (option?.AugmentationComponent) {
      setStack(getStackFromOption(option))
    } else {
      setStack([])
    }
  }

  const selectedOption = useMemo(() => stack.slice(-1)[0] || null, [stack])

  const selectedKey = selectedOption?.key || null

  return {
    stack,
    selectedKey,
    selectedOption,
    onSelect: handleSelectKey,
    options,
    structuredOptions: filteredStructuredOptions
  }
}

// Tweak the toolbar to include the disclosure button props.
export function useAugmentationDisclosedToolbar(
  toolbar: AugmentationToolbarHookReturn,
  disclosure: UseDisclosureReturn
): AugmentationToolbarHookReturn {
  // Clear the selected toolbar option, when the disclosure is closed
  // allowing the toolbar to once again show inline.
  const prevIsOpen = usePreviousDistinct(disclosure.isOpen)
  useEffect(() => {
    if (prevIsOpen === true) toolbar.onSelect(null)
  }, [disclosure.isOpen])

  useEffect(() => {
    // Open the disclosure when a toolbar option is selected.
    if (!disclosure.isOpen && toolbar.selectedKey) disclosure.onOpen()
    // Close the disclosure when no toolbar option is selected.
    if (disclosure.isOpen && !toolbar.selectedKey) disclosure.onClose()
  }, [toolbar.selectedKey])

  return {
    ...toolbar,
    options: toolbar.options.map((option) => ({
      ...option,
      ...disclosure.getButtonProps(),
      // TS: children in disclosure.getButtonProps() incompatible, so override.
      children: option.children,
      onClick: (e) => {
        toolbar.onSelect(option.key)
        option.onClick?.(e)
      }
    }))
  }
}

// Prune out props that would cause React warnings if passed to a DOM element.
function getOptionButtonProps(option: AugmentationToolbarOption) {
  const {
    key,
    icon,
    renderIcon,
    compositeKey,
    label,
    indicator,
    ToolbarComponent,
    AugmentationComponent,
    AugmentationNavComponent,
    ...rest
  } = option

  return rest
}

function handleRenderIcon(
  icon: IconName,
  context: ToolbarContext,
  renderIcon?: RenderIcon
) {
  const iconElement = <Pathicon icon={icon} size="1.4em" />
  return renderIcon ? renderIcon(iconElement, context) : iconElement
}

export function AugmentationToolbarDropdown({
  options,
  selectedKey,
  onSelect,
  defaultIsOpen
}: AugmentationToolbarHookReturn & { defaultIsOpen?: boolean }) {
  const { isOpen, onClose, getButtonProps, getDisclosureProps } = useDisclosure(
    { defaultIsOpen }
  )
  const selectedOption = options.find((option) => option.key === selectedKey)

  return selectedOption ? (
    <Popover isOpen={isOpen} onClose={onClose}>
      <PopoverTrigger>
        <Button
          size="sm"
          colorScheme="blackAlpha"
          variant="ghost"
          p={2}
          {...getButtonProps()}
        >
          <Heading
            as="h2"
            size="h6"
            className="theme-heading theme-heading-bold"
            fontWeight="bold"
          >
            <HStack>
              {/* For now, hide the icon */}
              {/* <Pathicon icon={selectedOption.icon} size="1.2em" /> */}
              <span>{selectedOption.label}</span>
              <Pathicon icon="chevron-down" size="1.2em" />
            </HStack>
          </Heading>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="auto"
          whiteSpace="nowrap"
          borderRadius="20px"
          {...getDisclosureProps()}
        >
          <PopoverBody>
            <VStack w="100%" alignItems="flex-start">
              {options
                .filter((option) => option.AugmentationComponent)
                .map((option) => (
                  <Button
                    key={option.key}
                    className="theme-heading theme-heading-bold"
                    as={Link}
                    size="sm"
                    colorScheme="blackAlpha"
                    variant={selectedKey === option.key ? "solid" : "ghost"}
                    leftIcon={handleRenderIcon(
                      option.icon,
                      "dropdown",
                      option.renderIcon
                    )}
                    p={2}
                    {...getOptionButtonProps(option)}
                    onClick={() => {
                      onSelect(option.key)
                      onClose()
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  ) : null
}

type AugmentationToolbarButtonProps = {
  option: AugmentationToolbarOption
  isSelected: boolean
  detailed?: boolean
}

export function AugmentationToolbarButton({
  option,
  isSelected,
  detailed
}: AugmentationToolbarButtonProps) {
  const { icon, renderIcon, label, indicator } = option
  const buttonProps: ButtonProps = {
    size: "sm",
    color: "blackAlpha.500",
    colorScheme: isSelected ? "whiteAlpha" : "blackAlpha",
    variant: isSelected ? "solid" : "ghost",
    ...getOptionButtonProps(option)
  }

  return detailed ? (
    <Button
      as={Link}
      className="AugmentationToolbarButton"
      w="100%"
      justifyContent="flex-start"
      // leftIcon={
      //   <Box pos="relative">
      //     {handleRenderIcon(icon, "toolbar", renderIcon)}
      //     {indicator && <Indicator />}
      //   </Box>
      // }
      {...buttonProps}
    >
      <HStack alignItems="center">
        <Flex pos="relative">
          {handleRenderIcon(icon, "toolbar", renderIcon)}
          {indicator && <Indicator top={0} right={0} />}
        </Flex>
        <span>{label}</span>
      </HStack>
    </Button>
  ) : (
    <IconButton
      as={Link}
      aria-label={label}
      className="AugmentationToolbarButton"
      roundSize={10}
      icon={handleRenderIcon(icon, "toolbar", renderIcon)}
      indicator={indicator}
      {...buttonProps}
    />
  )
}

function AugmentationToolbarItem({
  option,
  isSelected,
  detailed
}: {
  option: AugmentationToolbarOption
  isSelected: boolean
  detailed?: boolean
}) {
  const {
    // Pull out the toolbar option props to avoid React attribute warnings.
    compositeKey,
    ToolbarComponent,
    AugmentationComponent,
    AugmentationNavComponent,
    ...pruneOption
  } = option
  const ToolbarOptionComponent = ToolbarComponent || AugmentationToolbarButton

  return (
    <Tooltip
      key={pruneOption.key}
      // No tooltip when detailed since we show the label.
      title={detailed ? undefined : pruneOption.label}
      fitContent
      popover={{
        placement: "auto-start",
        openDelay: 1000
      }}
    >
      {/* Wrapper for ChakraTooltip PopoverTrigger to pass ref to. */}
      <Box w="100%">
        <ToolbarOptionComponent
          option={pruneOption}
          isSelected={isSelected}
          detailed={detailed}
        />
      </Box>
    </Tooltip>
  )
}

const ToolbarStack = styled(MotionStack)

export type AugmentationToolbarProps = {
  toolbar: AugmentationToolbarHookReturn
  alwaysVisible?: boolean
  detailed?: boolean
} & Omit<StackProps, "onSelect">

const AugmentationToolbar = React.forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(function AugmentationToolbar(
  { toolbar, alwaysVisible, direction, detailed, ...stackProps },
  ref
) {
  direction ||= "column"
  // Only permitting expansion in column mode.
  detailed = direction === "column" && !!detailed
  const { options, selectedKey, structuredOptions } = toolbar

  // Add state to control overflow
  const [isAnimating, setIsAnimating] = useState(true)

  // Create a key based on options to force re-mount when options change
  const optionsKey =
    options.map((option) => option.key).join(",") + `${detailed}`

  // Reset animation when options change (e.g., different viewport sizes)
  useEffect(() => {
    setIsAnimating(true)
  }, [optionsKey])

  // Animate the parent height/width dynamically.
  const parentDynamicSizeKey = direction === "column" ? "height" : "width"
  // Depinding on direction, handle animating the toolbar and options.
  // Animate the toolbar options in from the relative side.
  const childDynamicDirKey = direction === "column" ? "x" : "y"

  // Tweak total/child durations to taste.
  const totalDuration = 0.2
  const childDuration = 0.3
  const stagger = (totalDuration - childDuration) / (options.length - 1)

  const parentVariants = {
    collapsed: {
      [parentDynamicSizeKey]: 0
    },
    expanded: {
      [parentDynamicSizeKey]: "auto",
      transition: {
        duration: totalDuration,
        staggerChildren: -stagger
      }
    }
  }

  const childVariants = {
    collapsed: { opacity: 0, [childDynamicDirKey]: -50 },
    expanded: {
      opacity: 1,
      [childDynamicDirKey]: 0,
      transition: { duration: childDuration }
    }
  }

  // Group the toolbar options into palettes. Each group is either a single
  // option or an array of options.
  const paletteGroups = (
    structuredOptions.some(Array.isArray)
      ? structuredOptions.map((optionGroup) =>
          Array.isArray(optionGroup) ? optionGroup : [optionGroup]
        )
      : [structuredOptions]
  ) as AugmentationToolbarOption[][]

  const allIndexes = new Array(paletteGroups.length).fill(0).map((_, i) => i)
  // Expand first group by default, collapse others.
  const [expandedGroupIndexes, setExpandedGroupIndexes] = useState<number[]>(
    // When detailed, expand all groups.
    detailed ? allIndexes : [0]
  )

  // Expand a single group by index.
  function handleExpandGroup(index: number) {
    if (!expandedGroupIndexes.includes(index)) {
      setExpandedGroupIndexes([...expandedGroupIndexes, index])
      // Ensures the toolbar overflow is hidden while animating.
      setIsAnimating(true)
    }
  }

  // Determine if group is expanded (always expand if single item).
  function isGroupExpanded(index: number) {
    const group = paletteGroups[index]
    return expandedGroupIndexes.includes(index) || group.length === 1
  }

  // Using motion to animate to toolbar and its options. The container will server
  // as both a revealer of the toolbar and a means of setting up parent/child variants.
  // The idea is to animate the toolbar height while keeping overflow hidden to dynamically
  // reveal the toolbar itself. The options will animate in from the sied (left or top).
  return alwaysVisible || !selectedKey ? (
    <MotionStack
      key={optionsKey}
      ref={ref}
      className="AugmentationToolbar"
      spacing={2}
      direction={direction}
      variants={parentVariants}
      initial="collapsed"
      animate="expanded"
      onAnimationComplete={() => setIsAnimating(false)}
      borderRadius="card"
      overflow={isAnimating ? "hidden" : "visible"}
      bg={detailed ? "whiteAlpha.600" : "transparent"}
      backdropFilter={detailed ? "blur(10px)" : "none"}
      {...stackProps}
    >
      {paletteGroups
        .filter((paletteGroups) => paletteGroups.length)
        .map((optionGroup, i, list) => (
          <Stack
            key={i}
            spacing={2}
            direction={direction}
            p=".125em"
            bg={detailed ? "transparent" : "whiteAlpha.600"}
            backdropFilter={detailed ? "none" : "blur(10px)"}
            borderRadius="card"
            _hover={{
              bg: detailed ? "transparent" : "whiteAlpha.700"
            }}
          >
            <ToolbarStack
              direction={direction}
              spacing={1}
              className="ToolbarStack"
              position="relative"
            >
              {optionGroup
                .filter((option, index) => isGroupExpanded(i) || index === 0)
                .map((option, index) => {
                  const expandProps: MotionProps & BoxProps = !isGroupExpanded(
                    i
                  )
                    ? {
                        onClick: (e) => {
                          // Prevent toolbar button onClick firing.
                          e.preventDefault()
                          handleExpandGroup(i)
                        },
                        onMouseEnter: (e) => {
                          handleExpandGroup(i)
                        },
                        onAnimationComplete: () => setIsAnimating(false)
                      }
                    : {}

                  return (
                    <Box
                      as={motion.div}
                      key={option.key}
                      variants={childVariants}
                      pos="relative"
                      {...expandProps}
                    >
                      <AugmentationToolbarItem
                        option={options.find((o) => o.key === option.key)!}
                        isSelected={selectedKey === option.key}
                        detailed={detailed}
                      />
                      {!isGroupExpanded(i) && (
                        <Box pos="absolute" bottom={0} right={0}>
                          <Pathicon
                            icon="plus"
                            size=".8em"
                            color="blackAlpha.500"
                          />
                        </Box>
                      )}
                    </Box>
                  )
                })}
            </ToolbarStack>
            {i < list.length - 1 && detailed /* && !isAnimating */ && (
              <Divider m={0} />
            )}
          </Stack>
        ))}
    </MotionStack>
  ) : null
})

export default AugmentationToolbar
